import {useCallback, useEffect, useState} from "react";
import {PlatformApiError, type PlatformApiClient} from "../../api";
import { REVIEWBOOST_V3_UI_ENABLED } from "../../review-boost-v3-ui";

type Channel = "email" | "whatsapp";
interface QueueCounts {
  pending: number;
  dueBySchedule: number;
  notYetDue: number;
  cancelledSchedule: number;
  withoutSchedule: number;
  accepted: number;
  uncertain: number;
  blocked: number;
  processing: number;
}
interface QueueReadinessResponse {
  channels: Record<Channel, QueueCounts>;
  asOf: string;
  mode: "read_only";
  sendAuthorized: false;
  providerCalls: 0;
  backgroundWorkerInstalled: boolean;
  results?: {
    period: "lifetime";
    email: ResultCounts;
    whatsapp: ResultCounts;
    attributedReviews: null;
    verifiedHumanClicks: null;
  } | null;
  limitation: "schedule_counts_only_current_send_authorities_not_checked";
}
interface ResultCounts { delivered: number; failed: number; providerReportedClicks: number | null; optouts: number; }
const KEYS: Array<keyof QueueCounts> = [
  "pending", "dueBySchedule", "notYetDue", "cancelledSchedule",
  "withoutSchedule", "accepted", "uncertain", "blocked", "processing",
];
function validCounts(row: QueueCounts): boolean {
  return !!row && KEYS.every(key =>
    Number.isSafeInteger(row[key]) && row[key] >= 0)
    && row.dueBySchedule + row.notYetDue +
       row.cancelledSchedule + row.withoutSchedule === row.pending;
}
function valid(result: QueueReadinessResponse): boolean {
  return !!result && result.mode === "read_only"
    && result.sendAuthorized === false
    && result.providerCalls === 0
    && typeof result.backgroundWorkerInstalled === "boolean"
    && (!result.results || (result.results.period === "lifetime"
      && [result.results.email, result.results.whatsapp].every(row => row
        && [row.delivered,row.failed,row.optouts].every(n => Number.isSafeInteger(n) && n>=0)
        && (row.providerReportedClicks === null || (Number.isSafeInteger(row.providerReportedClicks) && row.providerReportedClicks>=0)))))
    && result.limitation === "schedule_counts_only_current_send_authorities_not_checked"
    && !Number.isNaN(Date.parse(result.asOf))
    && !!result.channels
    && validCounts(result.channels.email)
    && validCounts(result.channels.whatsapp);
}

/** Tenant-scoped counts only; this view cannot enqueue or send an invitation. */
export function CampaignDispatchReadinessPanel({
  slug, api, onExpired, compact = false,
}: {
  slug: string;
  api: PlatformApiClient;
  onExpired: () => void;
  /** Reveal per-channel counters on demand in the campaign editor. */
  compact?: boolean;
}) {
  const [data, setData] = useState<QueueReadinessResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "disabled" | "error">("loading");
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    let active = true;
    setStatus("loading");
    void api.reputation<QueueReadinessResponse>(
      slug, "/acquisition/dispatch/readiness",
    ).then(result => {
      if (!active) return;
      if (!valid(result)) { setData(null); setStatus("error"); return; }
      setData(result);
      setStatus("ready");
    }).catch(error => {
      if (!active) return;
      setData(null);
      if (error instanceof PlatformApiError && error.status === 401) {
        onExpired(); return;
      }
      setStatus(error instanceof PlatformApiError &&
        [403, 404].includes(error.status) ? "disabled" : "error");
    });
    return () => { active = false; };
  }, [slug, api, onExpired, revision]);

  const channelRows = status === "ready" && data !== null ? (
    <div className="rep-acquisition-saved">
      {(["email", "whatsapp"] as const).map(channel => {
        const row = data.channels[channel];
        return (
          <div className="rep-acquisition-saved-item" key={channel}>
            <strong>{channel === "email" ? "Email" : "WhatsApp"}</strong>
            <span>En cola: {row.pending}</span>
            <span>Con horario vencido: {row.dueBySchedule}</span>
            <span>Futuras: {row.notYetDue}</span>
            <span>Canceladas: {row.cancelledSchedule}</span>
            <span>Sin programación: {row.withoutSchedule}</span>
            <span>Aceptadas por proveedor: {row.accepted}</span>
            <span>Por reconciliar: {row.uncertain}</span>
            {data.results && <>
              <span>Entregas confirmadas por proveedor: {data.results[channel].delivered}</span>
              <span>Fallos confirmados por proveedor: {data.results[channel].failed}</span>
              <span>Bajas registradas: {data.results[channel].optouts}</span>
              <span>Clics informados por proveedor: {data.results[channel].providerReportedClicks ?? "No disponible"}</span>
            </>}
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <section className="rep-acquisition-panel" aria-label="Estado de preparación de envíos">
      <h3>Preparación de invitaciones</h3>
      <p>Resumen operativo sin datos de clientes. Un horario vencido no autoriza el envío: faltan las verificaciones de consentimiento, experiencia y proveedor.</p>
      {status === "loading" && <p role="status">Consultando colas…</p>}
      {status === "disabled" && <p>El diagnóstico de envíos no está habilitado para este negocio.</p>}
      {status === "error" && <p role="alert">No se pudo verificar el estado de las colas.</p>}
      {status === "ready" && data !== null && (
        <>
          <p>Consultado: {new Date(data.asOf).toLocaleString()} · Modo solo lectura</p>
          <p>{REVIEWBOOST_V3_UI_ENABLED ? "Servicio de envío" : "Despachador"}: {data.backgroundWorkerInstalled ? "Instalado para este negocio" : "No activo para este negocio"}.</p>
          {data.results && <p>Resultados acumulados. Entrega de email significa recepción por el servidor destinatario. Los clics pueden incluir verificadores automáticos; no prueban una reseña en Google.</p>}
          {compact ? (
            <details className="rep-acquisition-diagnostic-details">
              <summary>Ver desglose por canal: Email y WhatsApp</summary>
              {channelRows}
            </details>
          ) : channelRows}
          <p>Ningún envío se ejecuta desde esta pantalla.</p>
        </>
      )}
      <button type="button" disabled={status === "loading"}
        onClick={refresh}>{REVIEWBOOST_V3_UI_ENABLED ? "Actualizar estado" : "Actualizar diagnóstico"}</button>
    </section>
  );
}
